import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/config/branding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="reset-password-page">
        <div className="bg-white border-b-2 border-[#0A0A0A] px-6 py-3">
          <img src={LOGO_URL} alt="Self Certify UK" className="h-16 sm:h-20 object-contain object-left" />
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="form-container max-w-md text-center">
            <XCircle className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Invalid Reset Link</h2>
            <p className="text-sm text-[#71717A] mb-6">This password reset link is invalid or has expired.</p>
            <Link to="/forgot-password" className="brutalist-btn inline-block">Request New Link</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="reset-password-page">
      <div className="bg-white border-b-2 border-[#0A0A0A] px-6 py-3">
        <img src={LOGO_URL} alt="Self Certify UK" className="h-16 sm:h-20 object-contain object-left" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fade-in-up">
          {success ? (
            <div className="form-container text-center" data-testid="reset-success">
              <div className="w-16 h-16 bg-[#D4A537] flex items-center justify-center border-2 border-[#0A0A0A] mx-auto mb-6" style={{ boxShadow: '4px 4px 0px #0A0A0A' }}>
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Password Reset
              </h2>
              <p className="text-sm text-[#71717A] mb-8">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <Link to="/login" className="brutalist-btn w-full flex items-center justify-center" data-testid="go-to-login">
                Sign In
              </Link>
            </div>
          ) : (
            <div className="form-container" data-testid="reset-password-form">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Set New Password
              </h2>
              <p className="text-sm text-[#71717A] mb-8">
                Enter your new password below.
              </p>

              {error && (
                <div className="border-2 border-[#FF3B30] bg-red-50 p-3 mb-6 text-sm text-[#FF3B30]" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] font-bold text-[#71717A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    New Password
                  </label>
                  <input
                    data-testid="new-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="brutalist-input"
                    placeholder="Min. 6 characters"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] font-bold text-[#71717A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Confirm Password
                  </label>
                  <input
                    data-testid="confirm-password-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="brutalist-input"
                    placeholder="Re-enter password"
                    required
                  />
                </div>
                <button
                  data-testid="reset-submit-button"
                  type="submit"
                  disabled={loading}
                  className="brutalist-btn w-full flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
